'use client'

import {
  getTrackingCartItemPurchaseMode,
  getTrackingPromotionLabel,
} from '@/feature/cart/services/cart.tracking'
import { filterAvailableProducts } from '@/feature/product/services/product.filtering'
import { selectedReferenceAtom } from '@/feature/product/services/product.store'
import { getTrackingPageProductType } from '@/feature/product/services/product.tracking'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { PageTracking } from '@/lib/tracking/components/PageTracking.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { lastPageTrackedAtom, trackingConsentAtom } from '@/lib/tracking/services/tracking.store'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import type {
  ProductPageTracking_ProductBundleQuery,
  ProductPageTracking_ProductPriceAndStockQuery,
  ProductPageTracking_ProductQuery,
} from '@gql-generated/graphql'
import {
  ProductPageTracking_ProductBundleDocument,
  ProductPageTracking_ProductDocument,
  ProductPageTracking_ProductPriceAndStockDocument,
  ProductPageTracking_ProductWithReferenceDocument,
  ProductType,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useEffect, useRef } from 'react'

gql(/* GraphQL */ `
  fragment ProductPageAnalytics_Product on Product {
    reference
    name
    description
    type
    firstLevelCategory {
      name
    }
    breadcrumbs {
      name
      urlKey
    }
    brand {
      urlKey
      name
    }
    technicalSpecs {
      label
      value
    }
    equivalentProducts {
      reference
      name
    }
    supplierReference
    mainImage {
      ...FullPicture
    }
    images {
      ...FullPicture
    }
    filters {
      ...FiltersFields
    }
    nomenclatureFamily
    nomenclatureGroup
  }
`)

gql(/* GraphQL */ `
  query ProductPageTracking_ProductBundle($reference: String!) {
    productByReference(reference: $reference) {
      reference
      bundle {
        name
        reference
        stock {
          availability
        }
      }
    }
  }
`)

gql(/* GraphQL */ `
  query ProductPageTracking_Product($urlKey: String!) {
    product(urlKey: $urlKey) {
      ...ProductPageAnalytics_Product
    }
  }
`)

gql(/* GraphQL */ `
  query ProductPageTracking_ProductWithReference($reference: String!) {
    productByReference(reference: $reference) {
      ...ProductPageAnalytics_Product
    }
  }
`)

gql(/* GraphQL */ `
  query ProductPageTracking_ProductPriceAndStock($reference: String!) {
    productByReference(reference: $reference) {
      reference
      price {
        exclTax
      }
      parentReference
      stock {
        availability
      }
      canAddToCart
      bestArticlePrice
      analytics {
        promotionStatus
        profileCase
      }
    }
  }
`)

// Helper to map product data for analytics
function mapProductToAnalyticsItem({
  isAuthenticated,
  product,
  priceAndStockData,
  bundleData,
}: {
  isAuthenticated: boolean
  product: ProductPageTracking_ProductQuery['product']
  priceAndStockData: ProductPageTracking_ProductPriceAndStockQuery | undefined
  bundleData: ProductPageTracking_ProductBundleQuery | undefined
}) {
  const priceData = priceAndStockData?.productByReference
  const bundle = bundleData?.productByReference?.bundle

  return {
    item_id: product?.reference ?? '',
    item_code_p: priceData?.parentReference ?? '',
    item_nature: product?.type?.toLowerCase() || 'article',
    item_variants_amount: '',
    item_equivalent: product?.technicalSpecs && product?.equivalentProducts ? 'true' : 'false',
    item_bundle: bundle && filterAvailableProducts(bundle).length > 0 ? 'true' : 'false',
    item_name: product?.name || '',
    item_price:
      priceData?.price?.exclTax && priceData.price.exclTax > 0
        ? priceData.price.exclTax
        : priceData?.bestArticlePrice || 0,
    item_brand: product?.brand?.name || '',
    item_discount: '',
    item_category: product?.nomenclatureFamily || '',
    item_category2: product?.nomenclatureGroup || '',
    item_category3: '',
    item_list_name:
      getTrackingPageProductType({
        type: product?.type || ProductType.Product,
        filters: product?.filters || [],
      }) || '',

    item_promotion: getTrackingPromotionLabel(priceData?.analytics?.promotionStatus),
    item_public_price: isAuthenticated ? 'false' : 'true',
    item_purchase_mode: getTrackingCartItemPurchaseMode(
      priceData?.stock?.availability || [],
      priceData?.canAddToCart || false,
      true,
    ),

    item_market_price: '',
    item_acgc_price: '',
    item_stock_info: priceData?.analytics?.profileCase || '',
  }
}

type ProductPageAnalyticsProps = {
  slug: string
}

export function ProductPageAnalytics({ slug }: ProductPageAnalyticsProps) {
  const selectedReference = useAtomValue(selectedReferenceAtom)
  const isAuthenticated = useIsAuthenticated()
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)
  const lastPageTracked = useAtomValue(lastPageTrackedAtom)
  const hasPushedMainProductAnalyticsRef = useRef(false)

  const { data: trackingData, loading: trackingLoading } = useQuery(
    ProductPageTracking_ProductDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { urlKey: selectedReference || slug },
      skip: !!selectedReference || !isTrackingEnabled || isSSR,
    },
  )

  const { data: trackingDataByReference, loading: trackingByReferenceLoading } = useQuery(
    ProductPageTracking_ProductWithReferenceDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { reference: selectedReference || slug },
      skip: !selectedReference || !isTrackingEnabled || isSSR,
    },
  )

  //Fetch price and stock data (only when we have a reference)
  const priceReference = selectedReference || trackingData?.product?.reference

  const { data: priceAndStockData, loading: priceAndStockLoading } = useQuery(
    ProductPageTracking_ProductPriceAndStockDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { reference: priceReference ?? '' },
      fetchPolicy: 'cache-only',
      skip: !priceReference || !isTrackingEnabled || isSSR,
    },
  )

  const { data: bundleData, loading: bundleLoading } = useQuery(
    ProductPageTracking_ProductBundleDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { reference: priceReference ?? '' },
      skip: !priceReference || !isTrackingEnabled || isSSR,
    },
  )

  // Get the current product data based on which query is active
  const product = selectedReference
    ? trackingDataByReference?.productByReference
    : trackingData?.product
  const isLoading = selectedReference ? trackingByReferenceLoading : trackingLoading

  // Push analytics when page loads
  useEffect(() => {
    // Skip if data is still loading or product is not available
    if (isLoading || !product || !isTrackingEnabled || hasPushedMainProductAnalyticsRef.current) {
      return
    }

    // Wait for page tracking (users-data) to be completed for this specific product
    if (lastPageTracked.productReference !== product.reference) {
      return
    }

    // Wait for price and stock data to be available before pushing analytics
    if (priceAndStockLoading || !priceAndStockData) {
      return
    }

    // Wait for bundle data to be available before pushing analytics
    if (bundleLoading || !bundleData) {
      return
    }

    // Push the analytics event
    pushToGtmDataLayer({
      event: 'view_item',
      ecommerce: {
        currency: 'EUR',
        items: [
          mapProductToAnalyticsItem({
            isAuthenticated,
            product,
            priceAndStockData,
            bundleData,
          }),
        ],
      },
    })

    hasPushedMainProductAnalyticsRef.current = true
  }, [
    selectedReference,
    product,
    priceAndStockData,
    bundleData,
    isLoading,
    priceAndStockLoading,
    bundleLoading,
    isAuthenticated,
    isTrackingEnabled,
    lastPageTracked,
  ])

  if (!product) {
    return null
  }

  return (
    <PageTracking
      key={product.reference}
      productReference={product.reference}
      pageData={{
        pageType:
          getTrackingPageProductType({
            type: product.type,
            filters: product?.filters || [],
          }) || 'fiche article',
        pageCategory: product.firstLevelCategory?.name || '',
        pageSubCategory1: product.breadcrumbs?.[2]?.name || '',
        pageSubCategory2: product.breadcrumbs?.[3]?.name || '',
        pageSubCategory3: product.breadcrumbs?.[4]?.name || '',
      }}
    />
  )
}
